import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  XMarkIcon, PaperAirplaneIcon, PlusIcon, LockOpenIcon,
  ChevronRightIcon, PencilSquareIcon,
} from '@heroicons/react/24/outline';

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2,'0')}-${dt.toLocaleDateString('en-US',{month:'short'})}-${dt.getFullYear()}`;
};
const curYear  = () => { const y = new Date().getFullYear(); return `${y}-${y+1}`; };
const fmtMarks = (n) => (n === null || n === undefined || n === '') ? '—' : Number(n) % 1 === 0 ? String(Number(n)) : Number(n).toFixed(1);
const SUBJ_COLORS = ['#3b6ef8','#10c98e','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#84cc16','#f97316'];

const REQ_BANNER = (
  <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:10,padding:'10px 14px',fontSize:'.76rem',color:'#c2410c',marginBottom:'1.2rem',display:'flex',alignItems:'center',gap:8}}>
    📋 Add, unlock and marks-edit actions require admin approval before taking effect.
  </div>
);

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ca2 * { box-sizing:border-box; }
  .ca2 { font-family:'Sora',sans-serif; color:#1a1d2e; animation:ca2Fade .4s ease both; }
  @keyframes ca2Fade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .ca2-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .ca2-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .ca2-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-orange { background:#f97316; color:white; } .btn-orange:hover { box-shadow:0 4px 14px rgba(249,115,22,.3); }
  .btn-ghost  { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover  { background:#e4e7f0; }
  .btn-red    { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover { background:#ef4444; color:white; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .breadcrumb { display:flex; align-items:center; gap:6px; margin-bottom:1.2rem; flex-wrap:wrap; }
  .bc-item { font-size:.78rem; font-weight:600; color:#9094a8; cursor:pointer; transition:color .15s; }
  .bc-item:hover { color:#f97316; } .bc-item.active { color:#0f1123; cursor:default; }
  .bc-sep { color:#d1d5db; font-size:.82rem; }
  .card-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:1rem; margin-bottom:1rem; }
  .grade-card { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1.1rem 1.2rem; cursor:pointer; transition:all .15s; }
  .grade-card:hover { border-color:#f97316; box-shadow:0 4px 16px rgba(249,115,22,.1); transform:translateY(-1px); }
  .subj-card  { background:white; border-radius:14px; border:1px solid #e8ebf4; padding:1rem 1.2rem; cursor:pointer; transition:all .15s; }
  .subj-card:hover  { border-color:#8b5cf6; box-shadow:0 4px 16px rgba(139,92,246,.12); transform:translateY(-1px); }
  .gc-name { font-size:.95rem; font-weight:700; color:#0f1123; margin-bottom:3px; }
  .gc-sub  { font-size:.72rem; color:#9094a8; }
  .gc-arrow { display:flex; align-items:center; justify-content:space-between; margin-top:10px; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .row-item { display:flex; align-items:center; justify-content:space-between; padding:13px 18px; border-bottom:1px solid #f5f6fb; gap:10px; flex-wrap:wrap; cursor:pointer; transition:background .12s; }
  .row-item:last-child { border-bottom:none; }
  .row-item:hover { background:#fafbff; }
  .row-title   { font-size:.83rem; font-weight:600; color:#1a1d2e; }
  .row-sub     { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .row-desc    { font-size:.72rem; color:#6b7194; margin-top:3px; font-style:italic; }
  .row-actions { display:flex; gap:6px; align-items:center; flex-shrink:0; flex-wrap:wrap; }
  .status-badge { display:inline-flex; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; white-space:nowrap; }
  .status-badge.draft            { background:#f3f4f6; color:#6b7280; }
  .status-badge.submitted        { background:#ecfdf5; color:#10c98e; }
  .status-badge.locked           { background:#eef2ff; color:#3b6ef8; }
  .status-badge.unlock_requested { background:#fffbeb; color:#f59e0b; }
  .status-badge.unlocked         { background:#fdf4ff; color:#a855f7; }
  .clo-chip { display:inline-flex; align-items:center; gap:3px; font-size:.65rem; font-weight:700; padding:2px 7px; border-radius:6px; background:#eef2ff; color:#3b6ef8; font-family:'JetBrains Mono',monospace; white-space:nowrap; }
  .marks-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .marks-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
  .marks-table { width:100%; border-collapse:collapse; }
  .marks-table thead { background:#fafbff; border-bottom:2px solid #edf0f9; }
  .marks-table th { padding:10px 12px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .marks-table tbody tr { border-bottom:1px solid #f3f5fb; }
  .marks-table tbody tr:last-child { border-bottom:none; }
  .marks-table td { padding:8px 12px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .marks-input { width:80px; font-family:'JetBrains Mono',monospace; font-size:.82rem; padding:6px 8px; border:1.5px solid #e0e4f0; border-radius:7px; background:#f8f9fc; text-align:center; outline:none; }
  .marks-input:focus { border-color:#f97316; background:white; }
  .chk { width:16px; height:16px; cursor:pointer; }
  .student-name { font-weight:600; font-size:.8rem; color:#0f1123; }
  .student-roll { font-size:.7rem; color:#9094a8; margin-top:1px; font-family:'JetBrains Mono',monospace; }
  .marks-cards { display:none; padding:10px; }
  .marks-card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .mc-name { font-weight:700; font-size:.84rem; color:#0f1123; margin-bottom:2px; }
  .mc-roll { font-size:.7rem; color:#9094a8; font-family:'JetBrains Mono',monospace; margin-bottom:8px; }
  .mc-row  { display:flex; align-items:center; justify-content:space-between; font-size:.76rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .mc-row:last-child { border-bottom:none; }
  .mc-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  .mc-checks { display:flex; gap:16px; }
  .mc-check-item { display:flex; align-items:center; gap:5px; font-size:.76rem; color:#6b7194; }
  .coord-banner { background:#fff7ed; border:1px solid #fed7aa; border-radius:12px; padding:10px 16px; margin-bottom:14px; font-size:.78rem; color:#c2410c; }
  .ca2-empty   { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .ca2-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#f97316; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; overflow-y:auto; }
  .modal { background:white; border-radius:20px; padding:26px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:modalIn .25s ease; margin:auto; }
  @keyframes modalIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-btns { display:flex; gap:8px; justify-content:flex-end; margin-top:16px; flex-wrap:wrap; }
  .form-field { margin-bottom:12px; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; display:block; margin-bottom:5px; }
  .form-input,.form-select,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:#f97316; background:white; }
  .form-textarea { resize:vertical; min-height:70px; }
  .form-grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  @media (max-width:768px) {
    .ca2-title { font-size:1.1rem; }
    .card-grid { grid-template-columns:1fr 1fr; }
    .marks-table-wrap { display:none !important; }
    .marks-cards { display:block; }
    .marks-header { flex-direction:column; gap:10px; }
    .marks-actions .btn { flex:1; justify-content:center; }
    .form-grid-2 { grid-template-columns:1fr; }
    .modal-btns { justify-content:stretch; } .modal-btns .btn { flex:1; justify-content:center; }
  }
`;

export default function CoordAssessments() {
  // Navigation
  const [navStep,       setNavStep]      = useState('grades');
  const [activeClass,   setActiveClass]  = useState(null);
  const [activeSection, setActiveSection]= useState('');
  const [activeSubject, setActiveSubject]= useState('');

  // Data
  const [classes,       setClasses]      = useState([]);
  const [types,         setTypes]        = useState([]);
  const [subjects,      setSubjects]     = useState([]);
  const [assessments,   setAssessments]  = useState([]);
  const [selected,      setSelected]     = useState(null);
  const [marksData,     setMarksData]    = useState([]);
  const [loading,       setLoading]      = useState(true);
  const [saving,        setSaving]       = useState(false);
  const [obeEnabled,    setObeEnabled]   = useState(false);
  const [assessmentMode, setAssessmentMode] = useState('assessment');
  const [termPeriods,    setTermPeriods]    = useState([]);
  const isTermMode = assessmentMode === 'term';

  // Modals
  const [reqModal,      setReqModal]     = useState(null); // { type: 'unlock'|'marks', assessment, marksPayload? }
  const [reqNote,       setReqNote]      = useState('');
  const [addModal,      setAddModal]     = useState(false);
  const [addForm,       setAddForm]      = useState({
    assessmentTypeId:'', termPeriodId:'', subject:'', classId:'', section:'',
    date:'', maxMarks:'', description:'', academicYear: curYear(), countsTowardTerm: true,
  });

  useEffect(() => {
    api.get('/coordinator/classes').then(r => setClasses(r.data || [])).catch(()=>{});
    api.get('/coordinator/term-periods').then(r => setTermPeriods(r.data || [])).catch(()=>{});
    api.get('/coordinator/assessment-mode').then(r => {
      const mode = r.data?.assessmentMode || 'assessment';
      setAssessmentMode(mode);
      const kindParam = mode === 'term' ? '?kind=term_component' : '';
      api.get(`/coordinator/assessment-types${kindParam}`).then(r2 => setTypes(r2.data || [])).catch(()=>{});
    }).catch(() => {
      api.get('/coordinator/assessment-types').then(r2 => setTypes(r2.data || [])).catch(()=>{});
    });
    api.get('/coordinator/obe-settings').then(r => setObeEnabled(!!r.data?.obeEnabled)).catch(()=>{});
  }, []);

  // ── Fetch subjects for class+section ──────────────────────────────────────
  const fetchSubjects = useCallback(async (classId, section) => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ classId, section });
      const { data } = await api.get(`/coordinator/assessments?${p}`);
      const unique = [...new Set((Array.isArray(data) ? data : []).map(a => a.subject).filter(Boolean))].sort();
      setSubjects(unique);
    } catch { toast.error('Failed to load subjects'); }
    finally { setLoading(false); }
  }, []);

  // ── Fetch assessments for class+section+subject ───────────────────────────
  const fetchAssessments = useCallback(async (classId, section, subject) => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ classId, section, subject });
      const { data } = await api.get(`/coordinator/assessments?${p}`);
      setAssessments(Array.isArray(data) ? data : []);
    } catch { toast.error('Failed to load assessments'); }
    finally { setLoading(false); }
  }, []);

  // ── Navigation ────────────────────────────────────────────────────────────
  const goToGrades   = () => { setNavStep('grades'); setActiveClass(null); setActiveSection(''); setActiveSubject(''); };
  const goToSections = (cls) => { setActiveClass(cls); setActiveSection(''); setNavStep('sections'); };
  const goToSubjects = (sec) => {
    setActiveSection(sec);
    setNavStep('subjects');
    fetchSubjects(activeClass._id || activeClass.id, sec);
  };
  const goToList = (subject) => {
    setActiveSubject(subject);
    setNavStep('list');
    fetchAssessments(activeClass._id || activeClass.id, activeSection, subject);
  };

  // ── Open marks (read-only view with submit request button) ────────────────
  const openMarks = async (assessment) => {
    setLoading(true);
    try {
      // coordinator uses the same read endpoint — no admin marks endpoint
      const { data } = await api.get(`/coordinator/assessments/${assessment._id || assessment.id}`);
      setSelected(data);
      setMarksData((data.marks || []).map(m => ({
        studentId:     m.student?._id || m.student_id,
        name:          m.student?.user?.name || '—',
        rollNo:        m.student?.rollNo || '',
        admissionNo:   m.student?.admissionNo || '',
        marksObtained: m.marksObtained ?? m.marks_obtained ?? '',
        isAbsent:      m.isAbsent || m.is_absent || false,
        remarks:       m.remarks  || '',
      })));
      setNavStep('marks');
    } catch { toast.error('Failed to load marks'); }
    finally { setLoading(false); }
  };

  const updateMark = (idx, field, value) => {
    setMarksData(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const u = { ...r, [field]: value };
      if (field === 'isAbsent' && value) { u.marksObtained = ''; }
      return u;
    }));
  };

  // Submit marks edit as a request
  const handleSubmitMarksRequest = () => {
    const payload = marksData.map(r => ({
      studentId:    r.studentId,
      name:         r.name,
      marksObtained: r.isAbsent ? null : (r.marksObtained === '' ? null : Number(r.marksObtained)),
      isAbsent:     r.isAbsent,
      remarks:      r.remarks,
    }));
    setReqModal({ type: 'marks', assessment: selected, marksPayload: payload });
    setReqNote('');
  };

  // ── Submit any request ────────────────────────────────────────────────────
  const submitRequest = async () => {
    if (!reqNote.trim()) { toast.error('Reason is required'); return; }
    setSaving(true);
    try {
      const { type, assessment, marksPayload } = reqModal;

      if (type === 'marks') {
        await api.post('/coordinator/requests', {
          type: 'assessment_marks_edit',
          payload: {
            assessmentId:   assessment._id || assessment.id,
            assessmentName: assessment.assessmentType?.name,
            subject:        assessment.subject,
            className:      assessment.class?.name,
            section:        assessment.section,
            marks:          marksPayload,
            note:           reqNote,
          },
        });
      } else if (type === 'unlock') {
        await api.post('/coordinator/requests', {
          type: 'assessment_action',
          payload: {
            assessmentId:   assessment._id || assessment.id,
            action:         'unlock',
            assessmentName: assessment.assessmentType?.name,
            subject:        assessment.subject,
            className:      assessment.class?.name,
            currentStatus:  assessment.status,
            note:           reqNote,
          },
        });
      }

      toast.success('Request submitted — awaiting admin approval');
      setReqModal(null); setReqNote('');
      if (type === 'marks') setNavStep('list');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  // ── Submit add assessment request ─────────────────────────────────────────
  const submitAdd = async () => {
    if (!addForm.assessmentTypeId) { toast.error('Select assessment type'); return; }
    if (!addForm.subject.trim())   { toast.error('Subject is required');    return; }
    if (!addForm.classId)          { toast.error('Select a class');         return; }
    if (!addForm.section)          { toast.error('Select a section');       return; }
    if (!addForm.maxMarks)         { toast.error('Max marks is required');  return; }
    if (!addForm.description.trim()){ toast.error('Description is required'); return; }
    setSaving(true);
    try {
      const payload = { ...addForm, maxMarks: Number(addForm.maxMarks) };
      if (!isTermMode) { delete payload.termPeriodId; delete payload.countsTowardTerm; }
      await api.post('/coordinator/requests', {
        type: 'assessment_add',
        payload,
      });
      toast.success('Request submitted — awaiting admin approval');
      setAddModal(false);
      setAddForm({ assessmentTypeId:'', termPeriodId:'', subject:'', classId:'', section:'', date:'', maxMarks:'', description:'', academicYear: curYear(), countsTowardTerm: true });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const getSections = (cls) => {
    const found = cls || classes.find(c => (c._id||c.id) === addForm.classId);
    if (!found) return [];
    return (found.sections || []).map(s => typeof s === 'string' ? s : s.name || String(s));
  };

  // ── Breadcrumb ────────────────────────────────────────────────────────────
  const Breadcrumb = () => (
    <div className="breadcrumb">
      <span className="bc-item" onClick={goToGrades}>Assessments</span>
      {activeClass && <>
        <span className="bc-sep">›</span>
        <span className={`bc-item ${navStep==='sections'?'active':''}`}
          onClick={() => { setNavStep('sections'); setActiveSection(''); setActiveSubject(''); }}>
          {activeClass.name}
        </span>
      </>}
      {activeSection && <>
        <span className="bc-sep">›</span>
        <span className={`bc-item ${navStep==='subjects'&&!activeSubject?'active':''}`}
          onClick={() => { setNavStep('subjects'); setActiveSubject(''); fetchSubjects(activeClass._id||activeClass.id, activeSection); }}>
          Section {activeSection}
        </span>
      </>}
      {activeSubject && <>
        <span className="bc-sep">›</span>
        <span className={`bc-item ${navStep==='list'?'active':''}`} onClick={() => setNavStep('list')}>
          {activeSubject}
        </span>
      </>}
      {navStep === 'marks' && <><span className="bc-sep">›</span><span className="bc-item active">Marks</span></>}
    </div>
  );

  // ── MARKS VIEW ────────────────────────────────────────────────────────────
  if (navStep === 'marks' && selected) return (
    <div className="ca2"><style>{style}</style>
      <Breadcrumb />
      <div className="marks-header">
        <div>
          <div className="ca2-title">{selected.assessmentType?.name} — {selected.subject}</div>
          <div className="ca2-sub">
            {selected.class?.name} · Sec {selected.section}
            · Max: {fmtMarks(selected.max_marks ?? selected.maxMarks)}
            · {fmtDate(selected.date)}
            · By: <strong>{selected.createdBy?.name}</strong>
          </div>
          {selected.description && (
            <div style={{fontSize:'.76rem',color:'#6b7194',marginTop:4,fontStyle:'italic'}}>📝 {selected.description}</div>
          )}
          {obeEnabled && selected.cloMappings?.length > 0 && (
            <div style={{display:'flex',alignItems:'center',gap:5,marginTop:6,flexWrap:'wrap'}}>
              <span style={{fontSize:'.68rem',color:'#9094a8',fontWeight:600}}>CLOs:</span>
              {selected.cloMappings.map((m,i) => (
                <span key={i} className="clo-chip">{m.clo?.code||'CLO'}{m.weightage&&m.weightage!==100?` ${m.weightage}%`:''}</span>
              ))}
            </div>
          )}
        </div>
        <div className="marks-actions">
          <span className={`status-badge ${selected.status}`}>{selected.status?.replace('_',' ')}</span>
          <button className="btn btn-orange" onClick={handleSubmitMarksRequest}>
            <PaperAirplaneIcon />Submit Marks Edit Request
          </button>
        </div>
      </div>

      <div className="coord-banner">
        📋 Edit marks below then click <strong>Submit Marks Edit Request</strong>. Admin will review and apply the changes.
      </div>

      {marksData.length === 0 ? (
        <div className="ca2-empty">No students found for this assessment.</div>
      ) : (
        <div className="panel">
          {/* Desktop table */}
          <div className="marks-table-wrap" style={{overflowX:'auto'}}>
            <table className="marks-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Student</th>
                  <th>Marks / {fmtMarks(selected.max_marks ?? selected.maxMarks)}</th>
                  <th>Absent</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {marksData.map((m, i) => (
                  <tr key={m.studentId}>
                    <td style={{color:'#9094a8',fontFamily:"'JetBrains Mono',monospace",fontSize:'.74rem'}}>{i+1}</td>
                    <td>
                      <div className="student-name">{m.name}</div>
                      <div className="student-roll">Roll: {m.rollNo||'—'} · {m.admissionNo||''}</div>
                    </td>
                    <td>
                      <input type="number" className="marks-input"
                        value={m.isAbsent ? '' : (m.marksObtained ?? '')}
                        min={0} max={Number(selected.max_marks || selected.maxMarks) || 100}
                        disabled={m.isAbsent}
                        onChange={e => updateMark(i, 'marksObtained', e.target.value)} />
                    </td>
                    <td><input type="checkbox" className="chk" checked={m.isAbsent} onChange={e => updateMark(i,'isAbsent',e.target.checked)} /></td>
                    <td>
                      <input type="text" className="marks-input" style={{width:130}}
                        value={m.remarks} placeholder="remarks…"
                        onChange={e => updateMark(i,'remarks',e.target.value)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="marks-cards">
            {marksData.map((m, i) => (
              <div key={m.studentId} className="marks-card">
                <div className="mc-name">{m.name}</div>
                <div className="mc-roll">Roll: {m.rollNo||'—'} · {m.admissionNo||''}</div>
                <div className="mc-row">
                  <span className="mc-label">Marks / {fmtMarks(selected.max_marks??selected.maxMarks)}</span>
                  <input type="number" className="marks-input"
                    value={m.isAbsent?'':(m.marksObtained??'')}
                    min={0} max={Number(selected.max_marks||selected.maxMarks)||100}
                    disabled={m.isAbsent}
                    onChange={e => updateMark(i,'marksObtained',e.target.value)} />
                </div>
                <div className="mc-row">
                  <span className="mc-label">Status</span>
                  <div className="mc-checks">
                    <label className="mc-check-item"><input type="checkbox" checked={m.isAbsent} onChange={e=>updateMark(i,'isAbsent',e.target.checked)} /> Absent</label>
                  </div>
                </div>
                <div className="mc-row">
                  <span className="mc-label">Remarks</span>
                  <input type="text" className="marks-input" style={{width:130}}
                    value={m.remarks} placeholder="remarks…"
                    onChange={e => updateMark(i,'remarks',e.target.value)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Marks request confirmation modal */}
      {reqModal?.type === 'marks' && createPortal(
        <div className="modal-backdrop" onClick={() => setReqModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Submit Marks Edit Request</div>
              <button className="modal-close" onClick={() => setReqModal(null)}><XMarkIcon /></button>
            </div>
            <div style={{background:'#f8f9fc',border:'1px solid #e8ebf4',borderRadius:10,padding:12,marginBottom:14}}>
              <div style={{fontWeight:700,fontSize:'.88rem',color:'#0f1123'}}>{selected.assessmentType?.name}</div>
              <div style={{fontSize:'.76rem',color:'#9094a8',marginTop:4}}>
                {selected.subject} · {selected.class?.name} / {selected.section} · {selected.academicYear}
              </div>
              <div style={{fontSize:'.74rem',color:'#6b7194',marginTop:6}}>
                {reqModal.marksPayload?.length} student records will be included in this request.
              </div>
            </div>
            <div className="form-field">
              <label className="form-label">Note to admin</label>
              <textarea className="form-textarea" required value={reqNote} onChange={e=>setReqNote(e.target.value)}
                placeholder="Reason for editing marks…" />
            </div>
            <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:9,padding:'9px 12px',fontSize:'.76rem',color:'#c2410c'}}>
              📋 Admin will review the proposed marks and apply them if approved.
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setReqModal(null)}>Cancel</button>
              <button className="btn btn-orange" onClick={submitRequest} disabled={saving}>
                <PaperAirplaneIcon />{saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );

  // ── ASSESSMENT LIST ───────────────────────────────────────────────────────
  if (navStep === 'list') return (
    <div className="ca2"><style>{style}</style>
      <Breadcrumb />
      <div className="ca2-header">
        <div>
          <div className="ca2-title">{activeSubject}</div>
          <div className="ca2-sub">{activeClass?.name} · Section {activeSection}</div>
        </div>
        <button className="btn btn-orange" onClick={() => setAddModal(true)}><PlusIcon />Add Assessment</button>
      </div>
      {REQ_BANNER}
      <div className="panel">
        <div className="panel-head">
          <div className="panel-title">Assessments ({assessments.length})</div>
        </div>
        {loading ? (
          <div className="ca2-loading"><div className="spinner"/>Loading…</div>
        ) : assessments.length === 0 ? (
          <div className="ca2-empty">No assessments found for {activeSubject} in this section.</div>
        ) : assessments.map(a => (
          <div key={a._id||a.id} className="row-item" onClick={() => openMarks(a)}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                <div className="row-title">{a.assessmentType?.name}</div>
                <span className={`status-badge ${a.status}`}>{a.status?.replace('_',' ')}</span>
                {obeEnabled && a.cloMappings?.length > 0 && <>
                  {a.cloMappings.slice(0,3).map((m,i) => <span key={i} className="clo-chip">{m.clo?.code||'CLO'}</span>)}
                  {a.cloMappings.length > 3 && <span className="clo-chip">+{a.cloMappings.length-3}</span>}
                </>}
              </div>
              <div className="row-sub">
                {fmtDate(a.date)} · Max: {fmtMarks(a.max_marks??a.maxMarks)} · By: {a.createdBy?.name}
              </div>
              {a.description && <div className="row-desc">"{a.description}"</div>}
              {a.status==='unlock_requested' && (
                <div style={{fontSize:'.7rem',color:'#f59e0b',marginTop:3}}>⏳ Unlock request pending admin approval</div>
              )}
            </div>
            <div className="row-actions" onClick={e => e.stopPropagation()}>
              <button className="btn btn-ghost btn-sm" onClick={() => openMarks(a)}>
                <PencilSquareIcon />Marks
              </button>
              {(a.status==='locked'||a.status==='submitted'||a.status==='unlock_requested') && (
                <button className="btn btn-ghost btn-sm" onClick={() => { setReqModal({ type:'unlock', assessment:a }); setReqNote(''); }}>
                  <LockOpenIcon />{a.status==='unlock_requested'?'Re-unlock':'Unlock'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Unlock request modal */}
      {reqModal && reqModal.type !== 'marks' && createPortal(
        <div className="modal-backdrop" onClick={() => setReqModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">
                Request: Unlock Assessment
              </div>
              <button className="modal-close" onClick={() => setReqModal(null)}><XMarkIcon /></button>
            </div>
            <div style={{background:'#f8f9fc',border:'1px solid #e8ebf4',borderRadius:10,padding:12,marginBottom:14}}>
              <div style={{fontWeight:700,fontSize:'.88rem',color:'#0f1123'}}>{reqModal.assessment.assessmentType?.name}</div>
              <div style={{fontSize:'.76rem',color:'#9094a8',marginTop:4}}>
                {reqModal.assessment.subject} · {reqModal.assessment.class?.name} / {reqModal.assessment.section} · {reqModal.assessment.academicYear}
              </div>
            </div>
            <div className="form-field">
              <label className="form-label">Reason</label>
              <textarea className="form-textarea" required value={reqNote} onChange={e=>setReqNote(e.target.value)}
                placeholder="Reason for unlocking…" />
            </div>
            <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:9,padding:'9px 12px',fontSize:'.76rem',color:'#c2410c'}}>
              📋 This request will be sent to admin for approval.
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setReqModal(null)}>Cancel</button>
              <button className="btn btn-orange" onClick={submitRequest} disabled={saving}>
                <PaperAirplaneIcon />{saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* Add assessment modal */}
      {addModal && createPortal(
        <div className="modal-backdrop" onClick={() => setAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Request: Add Assessment</div>
              <button className="modal-close" onClick={() => setAddModal(false)}><XMarkIcon /></button>
            </div>
            <div className="form-field">
              <label className="form-label">{isTermMode ? 'Component' : 'Assessment Type'} *</label>
              <select className="form-select" value={addForm.assessmentTypeId} onChange={e=>setAddForm({...addForm,assessmentTypeId:e.target.value})}>
                <option value="">Select {isTermMode ? 'component' : 'type'}…</option>
                {types.map(t=><option key={t._id||t.id} value={t._id||t.id}>{t.name}</option>)}
              </select>
            </div>
            {isTermMode && (
              <div className="form-field">
                <label className="form-label">Term Period</label>
                <select className="form-select" value={addForm.termPeriodId} onChange={e=>setAddForm({...addForm,termPeriodId:e.target.value})}>
                  <option value="">No term (standalone entry)</option>
                  {termPeriods.filter(t=>t.active).map(t=><option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
                <div className="form-hint">Leave as "No term" if this assessment shouldn't count toward any term's report.</div>
              </div>
            )}
            {isTermMode && addForm.termPeriodId && (
              <div className="form-field">
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                  <input type="checkbox" className="chk" checked={addForm.countsTowardTerm}
                    onChange={e=>setAddForm({...addForm,countsTowardTerm:e.target.checked})} />
                  <span style={{fontSize:'.8rem',fontWeight:600,color:'#1a1d2e'}}>Count this toward the term's %</span>
                </label>
                <div style={{fontSize:'.7rem',color:'#9094a8',marginTop:3}}>
                  Uncheck for extra/practice entries you want recorded but not scored into the term total.
                </div>
              </div>
            )}
            <div className="form-field">
              <label className="form-label">Subject *</label>
              <input type="text" className="form-input" value={addForm.subject} onChange={e=>setAddForm({...addForm,subject:e.target.value})} placeholder="e.g. Mathematics" />
            </div>
            <div className="form-grid-2">
              <div className="form-field">
                <label className="form-label">Class *</label>
                <select className="form-select" value={addForm.classId} onChange={e=>setAddForm({...addForm,classId:e.target.value,section:''})}>
                  <option value="">Select…</option>
                  {classes.map(c=><option key={c._id||c.id} value={c._id||c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label className="form-label">Section *</label>
                <select className="form-select" value={addForm.section} onChange={e=>setAddForm({...addForm,section:e.target.value})} disabled={!addForm.classId}>
                  <option value="">Select…</option>
                  {getSections(classes.find(c=>(c._id||c.id)===addForm.classId)).map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label className="form-label">Date</label>
                <input type="date" className="form-input" value={addForm.date} onChange={e=>setAddForm({...addForm,date:e.target.value})} />
              </div>
              <div className="form-field">
                <label className="form-label">Max Marks *</label>
                <input type="number" className="form-input" value={addForm.maxMarks} onChange={e=>setAddForm({...addForm,maxMarks:e.target.value})} min="1" />
              </div>
            </div>
            <div className="form-field">
              <label className="form-label">Academic Year</label>
              <input type="text" className="form-input" value={addForm.academicYear} onChange={e=>setAddForm({...addForm,academicYear:e.target.value})} placeholder="2025-2026" />
            </div>
            <div className="form-field">
              <label className="form-label">Description *</label>
              <textarea className="form-textarea" value={addForm.description} onChange={e=>setAddForm({...addForm,description:e.target.value})} placeholder="Brief description…" />
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setAddModal(false)}>Cancel</button>
              <button className="btn btn-orange" onClick={submitAdd} disabled={saving}>
                <PaperAirplaneIcon />{saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );

  // ── SUBJECTS VIEW ─────────────────────────────────────────────────────────
  if (navStep === 'subjects' && activeClass) return (
    <div className="ca2"><style>{style}</style>
      <Breadcrumb />
      <div className="ca2-header">
        <div>
          <div className="ca2-title">Section {activeSection}</div>
          <div className="ca2-sub">{activeClass.name} · Select a subject</div>
        </div>
      </div>
      {loading ? (
        <div className="ca2-loading"><div className="spinner"/>Loading…</div>
      ) : subjects.length === 0 ? (
        <div className="ca2-empty">No assessments found for this section yet.</div>
      ) : (
        <div className="card-grid">
          {subjects.map((subj, i) => (
            <div key={subj} className="subj-card" onClick={() => goToList(subj)}>
              <div style={{width:32,height:32,borderRadius:9,background:`${SUBJ_COLORS[i%SUBJ_COLORS.length]}18`,color:SUBJ_COLORS[i%SUBJ_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.72rem',marginBottom:8}}>
                {subj.slice(0,2).toUpperCase()}
              </div>
              <div className="gc-name">{subj}</div>
              <div className="gc-arrow">
                <span style={{fontSize:'.72rem',color:SUBJ_COLORS[i%SUBJ_COLORS.length],fontWeight:600}}>View assessments</span>
                <ChevronRightIcon style={{width:15,height:15,stroke:SUBJ_COLORS[i%SUBJ_COLORS.length]}} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── SECTIONS VIEW ─────────────────────────────────────────────────────────
  if (navStep === 'sections' && activeClass) {
    const secs = getSections(activeClass);
    return (
      <div className="ca2"><style>{style}</style>
        <Breadcrumb />
        <div className="ca2-header">
          <div><div className="ca2-title">{activeClass.name}</div><div className="ca2-sub">Select a section</div></div>
        </div>
        {secs.length === 0 ? <div className="ca2-empty">No sections found.</div> : (
          <div className="card-grid">
            {secs.map((sec, i) => (
              <div key={sec} className="grade-card" onClick={() => goToSubjects(sec)}>
                <div style={{width:36,height:36,borderRadius:10,background:`${SUBJ_COLORS[i%SUBJ_COLORS.length]}18`,color:SUBJ_COLORS[i%SUBJ_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.9rem',marginBottom:8}}>
                  {sec}
                </div>
                <div className="gc-name">Section {sec}</div>
                <div className="gc-arrow">
                  <span style={{fontSize:'.72rem',color:SUBJ_COLORS[i%SUBJ_COLORS.length],fontWeight:600}}>View subjects</span>
                  <ChevronRightIcon style={{width:15,height:15,stroke:SUBJ_COLORS[i%SUBJ_COLORS.length]}} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── GRADE CARDS (root) ────────────────────────────────────────────────────
  return (
    <div className="ca2"><style>{style}</style>
      <div className="ca2-header">
        <div>
          <div className="ca2-title">Assessments</div>
          <div className="ca2-sub">Select a class to view and manage assessments</div>
        </div>
        <button className="btn btn-orange" onClick={() => setAddModal(true)}><PlusIcon />Add Assessment</button>
      </div>
      {REQ_BANNER}
      {loading ? (
        <div className="ca2-loading"><div className="spinner"/>Loading…</div>
      ) : classes.length === 0 ? (
        <div className="ca2-empty">No classes found.</div>
      ) : (
        <div className="card-grid">
          {classes.map((cls, i) => {
            const secs = getSections(cls);
            return (
              <div key={cls._id||cls.id} className="grade-card" onClick={() => goToSections(cls)}>
                <div style={{width:36,height:36,borderRadius:10,background:`${SUBJ_COLORS[i%SUBJ_COLORS.length]}18`,color:SUBJ_COLORS[i%SUBJ_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'.8rem',marginBottom:8}}>
                  {cls.name?.replace('Grade ','G').slice(0,3)}
                </div>
                <div className="gc-name">{cls.name}</div>
                <div className="gc-sub">{secs.length} section{secs.length!==1?'s':''}</div>
                <div className="gc-arrow">
                  <span style={{fontSize:'.72rem',color:'#f97316',fontWeight:600}}>View assessments</span>
                  <ChevronRightIcon style={{width:15,height:15,stroke:'#f97316'}} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add assessment modal at root level too */}
      {addModal && createPortal(
        <div className="modal-backdrop" onClick={() => setAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Request: Add Assessment</div>
              <button className="modal-close" onClick={() => setAddModal(false)}><XMarkIcon /></button>
            </div>
            <div className="form-field">
              <label className="form-label">{isTermMode ? 'Component' : 'Assessment Type'} *</label>
              <select className="form-select" value={addForm.assessmentTypeId} onChange={e=>setAddForm({...addForm,assessmentTypeId:e.target.value})}>
                <option value="">Select {isTermMode ? 'component' : 'type'}…</option>
                {types.map(t=><option key={t._id||t.id} value={t._id||t.id}>{t.name}</option>)}
              </select>
            </div>
            {isTermMode && (
              <div className="form-field">
                <label className="form-label">Term Period</label>
                <select className="form-select" value={addForm.termPeriodId} onChange={e=>setAddForm({...addForm,termPeriodId:e.target.value})}>
                  <option value="">No term (standalone entry)</option>
                  {termPeriods.filter(t=>t.active).map(t=><option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
                <div className="form-hint">Leave as "No term" if this assessment shouldn't count toward any term's report.</div>
              </div>
            )}
            {isTermMode && addForm.termPeriodId && (
              <div className="form-field">
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                  <input type="checkbox" className="chk" checked={addForm.countsTowardTerm}
                    onChange={e=>setAddForm({...addForm,countsTowardTerm:e.target.checked})} />
                  <span style={{fontSize:'.8rem',fontWeight:600,color:'#1a1d2e'}}>Count this toward the term's %</span>
                </label>
                <div style={{fontSize:'.7rem',color:'#9094a8',marginTop:3}}>
                  Uncheck for extra/practice entries you want recorded but not scored into the term total.
                </div>
              </div>
            )}
            <div className="form-field">
              <label className="form-label">Subject *</label>
              <input type="text" className="form-input" value={addForm.subject} onChange={e=>setAddForm({...addForm,subject:e.target.value})} placeholder="e.g. Mathematics" />
            </div>
            <div className="form-grid-2">
              <div className="form-field">
                <label className="form-label">Class *</label>
                <select className="form-select" value={addForm.classId} onChange={e=>setAddForm({...addForm,classId:e.target.value,section:''})}>
                  <option value="">Select…</option>
                  {classes.map(c=><option key={c._id||c.id} value={c._id||c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label className="form-label">Section *</label>
                <select className="form-select" value={addForm.section} onChange={e=>setAddForm({...addForm,section:e.target.value})} disabled={!addForm.classId}>
                  <option value="">Select…</option>
                  {getSections(classes.find(c=>(c._id||c.id)===addForm.classId)).map(s=><option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label className="form-label">Date</label>
                <input type="date" className="form-input" value={addForm.date} onChange={e=>setAddForm({...addForm,date:e.target.value})} />
              </div>
              <div className="form-field">
                <label className="form-label">Max Marks *</label>
                <input type="number" className="form-input" value={addForm.maxMarks} onChange={e=>setAddForm({...addForm,maxMarks:e.target.value})} min="1" />
              </div>
            </div>
            <div className="form-field">
              <label className="form-label">Academic Year</label>
              <input type="text" className="form-input" value={addForm.academicYear} onChange={e=>setAddForm({...addForm,academicYear:e.target.value})} placeholder="2025-2026" />
            </div>
            <div className="form-field">
              <label className="form-label">Description *</label>
              <textarea className="form-textarea" value={addForm.description} onChange={e=>setAddForm({...addForm,description:e.target.value})} placeholder="Brief description…" />
            </div>
            <div className="modal-btns">
              <button className="btn btn-ghost" onClick={() => setAddModal(false)}>Cancel</button>
              <button className="btn btn-orange" onClick={submitAdd} disabled={saving}>
                <PaperAirplaneIcon />{saving ? 'Submitting…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}